<template>
  <div id="past-due-clients">
    <div class="list" ref="list">
      <table>
        <thead>
          <tr>
            <th colspan="2">
              <div class="d-flex justify-content-center">
                {{ $t('CLT_ID') }}
                <div class="arrow" :class="{down: filters.sortedList.includes('-mifos_id')}"><font-awesome-icon @click="sort('mifos_id')" :icon="faSortUp" mask="circle"/></div>
                <div class="number-circle"><span>{{ getIndexSort('mifos_id') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('FULL_NAME') }}
                <div class="arrow" :class="{down: filters.sortedList.includes('-name')}"><font-awesome-icon @click="sort('name')" :icon="faSortUp" mask="circle"/></div>
                <div class="number-circle"><span>{{ getIndexSort('name') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('DUE_CNTRTS') }}
                <div class="arrow" :class="{down: filters.sortedList.includes('-total_loans')}"><font-awesome-icon @click="sort('total_loans')" :icon="faSortUp" mask="circle"/></div>
                <div class="number-circle"><span>{{ getIndexSort('total_loans') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('LATEST_DUE_DATE') }}
                <div class="arrow" :class="{down: filters.sortedList.includes('-due_date_latest')}"><font-awesome-icon @click="sort('due_date_latest')" :icon="faSortUp" mask="circle"/></div>
                <div class="number-circle"><span>{{ getIndexSort('due_date_latest') }}</span></div>
              </div>
            </th>
            <th class="call-results">{{ $t('LATEST_PAYMENT_REMINDER_RESULT') }}</th>
            <th class="call-results">{{ $t('NEXT_ACTION') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr :key="key" v-for="(item, key) in items" @click="showDetails(item)">
            <td
              class="status bullet-wrapper"
              :class="{
                'value-adjustment': item.hasValueAdjustment,
                'waiting-to-liquidize': item.waitingToLiquidize
              }"
            ><div class="bullet">•</div></td>
            <td
              class="status"
              :class="{
                'value-adjustment': item.hasValueAdjustment,
                'waiting-to-liquidize': item.waitingToLiquidize
              }"
            >{{ item.finfluxId }}</td>
            <td>
              <div class="mobile-header" v-if="!isDesktop">{{ $t('FULL_NAME') }}:</div>
              <div>{{ item.nameVi }}</div></td>
            <td>
              <div class="mobile-header" v-if="!isDesktop">{{ $t('DUE_CNTRTS') }}:</div>
              <div>{{ item.numberOfDueContracts }}</div>
            </td>
            <td>
              <div class="mobile-header" v-if="!isDesktop">{{ $t('MOST_DUE_DATE') }}:</div>
              <div>{{ item.latestDate }}</div>
            </td>
            <td class="call-results">
              <div class="mobile-header" style="white-space: normal;" v-if="!isDesktop">{{ $t('LATEST_PAYMENT_REMINDER_RESULT') }}:</div>
              <p v-if="item.lastResult.status.key">
                <span class="grey">{{ formatDateTime(item.lastResult.createdAt) }}</span>
                <span class="grey"> - </span>
                <span class="grey">{{ item.lastResult.createdBy.firstName }} {{ item.lastResult.createdBy.lastName }}</span>
                <br/>
                <span>{{ $t(item.lastResult.status.key) }}</span>
                <span> | </span>
                <span>{{ $t(item.lastResult.clientAttitude.key) }}</span>
              </p>
            </td>
            <td class="call-results" :class="{ marginTop: item.lastResult.status.key === ''}">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('NEXT_ACTION') }}:</div>
              <p v-if="item.lastResult.nextAction && item.lastResult.nextActionAt">
                <span class="grey">{{ getActionTimeContent(item.lastResult.nextActionAt) }}</span>
                <br/>
                <span>{{ $t(item.lastResult.nextAction.key) }}</span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      :page="filters.page"
      :pageSize="filters.pageSize"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />

    <live-update-controller channel="payment-reminder" @signal="(e) => checkSignal(e)" />
  </div>
</template>

<script>
import { watch, onMounted, ref } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';

import { DATE_FORMAT, DISPLAY_DATE_TIME_FORMAT } from '@/consts';
import { scrollToTop } from '@/helpers';

import Pagination from '@/components/Pagination';
import LiveUpdateController from '@/components/LiveUpdateController';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { faSortUp } from '@fortawesome/free-solid-svg-icons';

const { useState, useActions } = createNamespacedHelpers('paymentReminder');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useState: useAppState } = createNamespacedHelpers('app');

const MESSAGE_TYPES = {
  PAYMENT_REMINDER_RESULT: 'PAYMENT_REMINDER_RESULT',
  CONTRACT: 'CONTRACT'
};

export default {
  name: 'PastDueClients',
  components: {
    Pagination,
    LiveUpdateController,
    FontAwesomeIcon,
  },
  setup(props, { root }) {
    const { total, items, filters } = useState(['total', 'items', 'filters']);
    const { isDesktop } = useAppState(['isDesktop']);
    const { applyFilter, getPaymentReminder, targetClient } = useActions(['applyFilter', 'getPaymentReminder', 'targetClient']);
    const { error: errorFlash } = useFlashActions(['error']);
    const { togglePaymentReminderClientDetailsModal: toggleDetails } = useModalActions(['togglePaymentReminderClientDetailsModal']);

    const list = ref(null);

    const sort = (name) => {
      const { sortedList } = filters.value;
      let index = sortedList.indexOf(name);
      if (index >= 0) {
        sortedList.splice(index, 1);
        sortedList.unshift(`-${name}`);
      } else {
        index = sortedList.indexOf(`-${name}`);
        sortedList.splice(index, 1);
        sortedList.unshift(name);
      }
      applyFilter({ sortedList });
    };

    const getIndexSort = (name) => {
      const index = filters.value.sortedList.indexOf(name);
      if (index >= 0) {
        return index + 1;
      }
      return filters.value.sortedList.indexOf(`-${name}`) + 1;
    };

    const getItems = () => {
      try {
        getPaymentReminder();
      } catch (error) {
        errorFlash(error.message);
      }
    };

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const formatDateTime = (date) => {
      const formatedDate = moment(date);
      if (formatedDate.isValid()) {
        return formatedDate.format('DD/MM HH:mm');
      }
      return '';
    };

    const showDetails = (client) => {
      targetClient(client);
      toggleDetails();
    };

    const getActionTimeContent = (time) => {
      const nextActionAt = moment(time).startOf('day');

      const diffTime = nextActionAt.diff(moment().startOf('day'), 'days');

      if (diffTime < 0) {
        return moment(time).format(DISPLAY_DATE_TIME_FORMAT);
      }

      switch (diffTime) {
        case 0:
          return root.$t('TODAY');
        case 1:
          return root.$t('TOMORROW');
        default:
          return root.$t('AFTER_N_DAY', { days: diffTime });
      }
    };

    const checkSignal = (e) => {
      const { message } = JSON.parse(e.data);

      if (message.type === MESSAGE_TYPES.PAYMENT_REMINDER_RESULT) {
        items.value.some((item) => {
          if (item.id === message.client) {
            getPaymentReminder(false);
            return true;
          }
          return false;
        });
      } else if (message.type === MESSAGE_TYPES.CONTRACT) {
        const fromDate = moment(filters.value.fromDate, DATE_FORMAT);
        const toDate = moment(filters.value.toDate, DATE_FORMAT);
        const dueDate = moment(message.dueDate, DATE_FORMAT);

        const conditions = [
          filters.value.minApprovedAmount !== null && filters.value.minApprovedAmount > message.approvedAmount,
          filters.value.maxApprovedAmount !== null && filters.value.maxApprovedAmount < message.approvedAmount,
          filters.value.branches.length !== 0 && !filters.value.branches.includes(message.branch),
          filters.value.assetTypes.length !== 0 && !filters.value.assetTypes.includes(message.assetType),
          filters.value.assetTypes.length !== 0 && !filters.value.assetTypes.includes(message.assetType),
          fromDate > dueDate || toDate < dueDate,
          filters.value.hasValueAdjustment !== message.hasValueAdjustment
        ];

        if (conditions.includes(true)) {
          return;
        }

        getPaymentReminder(false);
      }
    };

    onMounted(() => {
      getPaymentReminder();
    });

    watch(filters, (newFilters, prevFilters) => {
      getItems();

      if (newFilters.page !== prevFilters.page) {
        scrollToTop(list.value);
      }
    });

    return {
      items,
      filters,
      total,
      isDesktop,
      checkSignal,
      changePageSize,
      changePage,
      formatDateTime,
      showDetails,
      getActionTimeContent,
      sort,
      getIndexSort,
      faSortUp,
      list
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#past-due-clients {
  .list {
    margin-bottom: 20px;
    max-height: calc(100vh - 325px);
    overflow-y: auto;

    .arrow{
      cursor: pointer;
      width: 32px;
      height: 28px;
      transform: rotate(0deg);
      transform-origin: center;
      transition: all .3s ease-in-out;
      // border: 1px solid $alto;

      svg {
        text-align: center;
        font-size: 28px;
        margin-top: 5px;
        height: 100%;
        width: 100%;
      }

      &.down {
        transform: rotate(180deg);
        transform-origin: center;

        svg {
          margin-top: 8px;
        }
      }
    }


    .number-circle {
      span {
        color: $babyPowder;
        margin-left: 2px;
        background: $lavenderGrey;
        border-radius: 50%;
        -moz-border-radius: 50%;
        -webkit-border-radius: 50%;
        display: inline-block;
        font-weight: bold;
        line-height: 14px;
        text-align: center;
        font-size: 14px;
        width: 14px;
      }
    }

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

        tr {
          background-color: $babyPowder;

          th {
            padding: 15px 10px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: $babyPowder;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;

            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }

            &.call-results {
              text-align: left;
            }
          }
        }
      }

      tbody {
        text-align: center;

        tr {
          background-color: $babyPowder;
          cursor: pointer;
          transition: all .2s ease-in-out;

          &:hover {
            background-color: $ghostWhite;
          }

          td {
            padding: 15px 5px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
              color: $geranium;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto
            }

            &.call-results {
              text-align: left;

              p {
                margin: 0;
                font-size: 14px;

                .grey {
                  color: $lavenderGrey;
                }
              }
            }

            &.status {
              color: $shamrockGreen;

              .bullet {
                font-size: 36px;
                padding-right: 0;
                padding-left: 10px;
                transform: translateY(-2px);
                vertical-align: middle;
                display: inline-block;
              }

              &.value-adjustment {
                color: $paradisePink;
              }

              &.waiting-to-liquidize {
                color: $lavenderGrey;
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 991px) {
    .list {
      margin-bottom: 10px;
      display: block;

      table {
        width: auto;
        display: block;

        thead {
          display: block;
          tr {
            position: absolute;
            top: -9999px;
            left: -9999px;
          }
        }

        tbody {
          text-align: start;
          display: block;

          tr {
            display: block;
            border-top: 1px solid $alto;
            border-bottom: 1px sold $alto;

            &:nth-child() {
              background-color: grey;
            }

            td {
              padding: 5px;
              display:block;
              border: none;
              position: relative;
              padding-left: 53%;
              min-height: 30px;

              &::before {
                position: absolute;
                top: 0;
                left: 6px;
                width: 45%;
                padding-right: 10px;
                white-space: nowrap;
                text-align: center;
              }

              .mobile-header {
                position: absolute;
                left: 6px;
                padding-right: 10px;
                white-space: nowrap;
                text-align: start;
                padding-left: 10px;
                width: 133px;
              }

              &.marginTop {
                margin-top: 25px;
              }

              &:first-child {
                border: 0;
                padding-top: 20px;
              }

              &:last-child {
                border: 0;
                padding-bottom: 20px;
                @media (max-width: 991px) {
                  padding-bottom: 35px;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
